import * as React from 'react';
import { Paper, Typography, Button } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import AccountBlock from './account-block';
import DomainBadge from './domain-badge';
import ScopeChecklist from './scope-checklist';
import SendingLabel from './sending-label';
import { useConsentStyles } from './styles';
import { splitAroundClient } from './translations';

const ALLOW_HELP_ID = 'allow-help';

// Consent card of a pending request: every requested scope starts checked and
// the user can only uncheck. `sending` is 'approve', 'deny', 'switch' (switching
// account) or null.
export default function ConsentCard({ request, brandName, sending, actionError, onApprove, onDeny, onSwitchAccount }) {
  const { t } = useTranslation();
  const classes = useConsentStyles();
  const [checked, setChecked] = React.useState(() => (
    request.scopes.reduce((acc, item) => ({ ...acc, [item.scope]: true }), {})
  ));
  const selected = request.scopes.filter((item) => checked[item.scope]).map((item) => item.scope);
  const noneSelected = selected.length === 0;
  const isSending = !!sending;
  const titleParts = splitAroundClient(t, 'mcp.consent.title', { brand: brandName });
  const { account, client } = request;

  return (
    <Paper elevation={3} className={classes.card}>
      <Typography variant="h5" component="h1" className={classes.literal} gutterBottom>
        {titleParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className={classes.literal}>{client.name}</span>}
            {part}
          </React.Fragment>
        ))}
      </Typography>

      {/* Same predicate as the backend: only a CIMD client has a verified
          domain. A missing or unknown type keeps the warning (fail closed). */}
      <DomainBadge
        label={t('mcp.consent.domainLabel')}
        domain={client.domain}
        unverified={client.registration_type !== 'cimd'}
      />

      <AccountBlock
        name={[account.name, account.surname].filter(Boolean).join(' ')}
        email={account.email}
        onSwitchAccount={onSwitchAccount}
        disabled={isSending}
      />

      <ScopeChecklist
        scopes={request.scopes}
        checked={checked}
        disabled={isSending}
        onToggle={(scope) => setChecked((previous) => ({ ...previous, [scope]: !previous[scope] }))}
      />

      {/* Only while meetings:write is requested and still checked. */}
      {checked['meetings:write'] && (
        <Alert severity="warning" className={classes.alert}>
          {t('mcp.consent.meetingsWriteNotice')}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" className={classes.alert}>
          {t('mcp.consent.actionError')}
        </Alert>
      )}

      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          className={classes.btn}
          disabled={noneSelected || isSending}
          aria-describedby={noneSelected ? ALLOW_HELP_ID : undefined}
          onClick={() => onApprove(selected)}
        >
          {sending === 'approve' ? <SendingLabel /> : t('mcp.consent.allow')}
        </Button>
        <Button variant="outlined" className={`${classes.btn} ${classes.deny}`} disabled={isSending} onClick={onDeny}>
          {sending === 'deny' ? <SendingLabel /> : t('mcp.consent.deny')}
        </Button>
      </div>
      {noneSelected && (
        <Typography id={ALLOW_HELP_ID} variant="caption" color="textSecondary" component="p" className={classes.help}>
          {t('mcp.consent.selectAtLeastOne')}
        </Typography>
      )}
    </Paper>
  );
}
